import * as React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";

import { SUCCESS_EVENT_TYPE, ERROR_EVENT_TYPE } from "./LazyImageContextProvider";

type Props = {
  src: string;
  background?: boolean;
  fallbackUrl?: string;
} & React.ImgHTMLAttributes<any>;

interface State {
  isLoaded: boolean;
  visible: boolean;
  hasError: boolean;
}

class LazyImage extends React.PureComponent<Props, State> {
  public static contextTypes = {
    getLazyImageIntersectionObserver: PropTypes.func,
  };

  private ref: HTMLElement | null = null;
  private visibleTimer: number = 0;

  public constructor(props: Props) {
    super(props);

    this.state = {
      isLoaded: false,
      visible: false,
      hasError: false,
    };
  }

  public componentDidMount() {
    (this.context.getLazyImageIntersectionObserver() as IntersectionObserver).observe(this.ref!);
    this.ref!.addEventListener(SUCCESS_EVENT_TYPE, this.onLoad);

    if (this.props.fallbackUrl) {
      this.ref!.addEventListener(ERROR_EVENT_TYPE, this.onLoadError);
    }
  }

  public componentWillUnmount() {
    this.ref!.removeEventListener(SUCCESS_EVENT_TYPE, this.onLoad);
    if (this.props.fallbackUrl) {
      this.ref!.removeEventListener(ERROR_EVENT_TYPE, this.onLoadError);
    }
    if (this.visibleTimer) {
      clearTimeout(this.visibleTimer);
    }
  }

  public setRef = (ref: HTMLElement | null) => {
    this.ref = ref;
  };

  public onLoad = () => {
    this.setState({ isLoaded: true }, () => {
      this.visibleTimer = window.setTimeout(() => {
        this.setState({
          visible: true,
        });
      }, 30);
    });
  };

  public onLoadError = () => {
    this.setState({ isLoaded: true, hasError: true }, () => {
      this.visibleTimer = window.setTimeout(() => {
        this.setState({ visible: true });
      }, 30);
    });
  };

  public render() {
    const { src, background, fallbackUrl, ...rest } = this.props;
    const { isLoaded, visible, hasError } = this.state;
    if (background) {
      return (
        <Div
          innerRef={this.setRef}
          data-src={src}
          style={{
            backgroundImage: isLoaded ? `url("${hasError ? fallbackUrl : src}")` : undefined,
            opacity: visible ? 1 : 0,
          }}
          {...rest}
        />
      );
    }

    return (
      <Img
        src={isLoaded ? (hasError ? fallbackUrl : src) : undefined}
        data-src={src}
        innerRef={this.setRef}
        style={{ opacity: visible ? 1 : 0 }}
        {...rest}
      />
    );
  }
}

export default LazyImage;

const Img = styled.img`
  transition-property: opacity;
  transition-duration: 150ms;
  transition-timing-function: linear;
  opacity: 0;
`;

const Div = styled.div`
  transition-property: opacity;
  transition-duration: 150ms;
  transition-timing-function: linear;
  opacity: 0;
`;



// WEBPACK FOOTER //
// ./frontend/src/common/components/lazy/LazyImage.tsx